import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { MessageCategoryConfig } from '@gooduncles/gu-app-schema';
import { Avatar, Button, Empty, Popconfirm, Space, Spin, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  deleteChannelCategory,
  observeChannelCategoryConf,
  setChannelCategoryConf,
} from 'src/utils/firebase/firebase-short-cut';
import { AuditLogger } from 'src/utils/audit-logger';

import ChannelCategoryForm from './ChannelCategoryForm';

import styles from './ChannelCategoryConfig.module.scss';

const auditLogger = AuditLogger.getInstance();

interface CategoryRow {
  key: string;
  label: string;
  iconUrl: string;
}

const ChannelCategoryConfig: FC = () => {
  const [categories, setCategories] = useState<Record<string, MessageCategoryConfig>>({});
  const [loading, setLoading] = useState(true);
  const [docExists, setDocExists] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<{ key: string; config: MessageCategoryConfig } | null>(null);

  useEffect(() => {
    const subscription = observeChannelCategoryConf().subscribe({
      next: (doc) => {
        if (doc) {
          setCategories(doc.categories ?? {});
          setDocExists(true);
        } else {
          setCategories({});
          setDocExists(false);
        }
        setLoading(false);
      },
      error: () => setLoading(false),
    });
    return () => subscription.unsubscribe();
  }, []);

  const dataSource: CategoryRow[] = useMemo(
    () =>
      Object.entries(categories).map(([key, config]) => ({
        key,
        label: config.label,
        iconUrl: config.iconUrl,
      })),
    [categories],
  );

  const existingKeys = useMemo(() => Object.keys(categories), [categories]);

  const handleCreate = useCallback(async () => {
    if (!docExists) {
      await setChannelCategoryConf({ categories: {} });
    }
    setEditingCategory(null);
    setFormOpen(true);
  }, [docExists]);

  const handleEdit = useCallback(
    (row: CategoryRow) => {
      setEditingCategory({
        key: row.key,
        config: categories[row.key],
      });
      setFormOpen(true);
    },
    [categories],
  );

  const handleDelete = useCallback(async (categoryKey: string) => {
    try {
      await deleteChannelCategory(categoryKey);
      auditLogger.log({
        action: 'DELETE',
        entity: 'config',
        entityId: 'channelCategory',
        summary: `채널 카테고리 삭제: ${categoryKey}`,
      });
      message.success('카테고리가 삭제되었습니다.');
    } catch (error) {
      console.error('Failed to delete category:', error);
      message.error('카테고리 삭제에 실패했습니다.');
    }
  }, []);

  const handleClose = useCallback(() => {
    setFormOpen(false);
    setEditingCategory(null);
  }, []);

  const columns: ColumnsType<CategoryRow> = [
    {
      title: '아이콘',
      dataIndex: 'iconUrl',
      key: 'iconUrl',
      width: 80,
      render: (iconUrl: string) => <Avatar size={32} src={iconUrl} />,
    },
    {
      title: '카테고리 키',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: '표시 이름',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type='text' size='small' icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title='카테고리 삭제'
            description={`"${record.label}" 카테고리를 삭제하시겠습니까?`}
            onConfirm={() => handleDelete(record.key)}
            okText='삭제'
            cancelText='취소'
            okButtonProps={{ danger: true }}
          >
            <Button type='text' size='small' danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div className='height100 flexRow flexCenter'>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>채널 카테고리 설정</span>
        <Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
          카테고리 추가
        </Button>
      </div>
      {dataSource.length === 0 ? (
        <Empty description='등록된 카테고리가 없습니다.' />
      ) : (
        <Table columns={columns} dataSource={dataSource} pagination={false} size='middle' />
      )}
      <ChannelCategoryForm
        open={formOpen}
        onClose={handleClose}
        editingCategory={editingCategory}
        existingKeys={existingKeys}
      />
    </div>
  );
};

export default ChannelCategoryConfig;
